// Components/ChatContacts/components/ContactItem.jsx
import React from "react";
import { Box, Divider, Avatar, ListItemText, ListItemButton, Typography, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Icono para indicar selección

const ContactItem = ({
    contact,
    isLoading,
    isLoaded,
    setContactRef,
    selectUser,
    isMobile,
    isActive // Nueva prop para indicar si está seleccionado
}) => {
    const notLoadedYet = !isLoading && !isLoaded;

    // Formateamos la fecha y hora según las reglas:
    // 1. Si es hoy: solo hora
    // 2. Si es ayer: texto "AYER"
    // 3. Si es anterior: solo fecha

    let displayTime = '';
    let displayDate = '';

    if (contact.lastMessageTime) {
        const messageDate = new Date(contact.lastMessageTime);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        // Resetear las horas para comparar solo las fechas
        const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());

        if (messageDateOnly.getTime() === todayDate.getTime()) {
            // Es hoy: mostrar solo la hora
            displayTime = messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            displayDate = ''; // No mostrar fecha
        } else if (messageDateOnly.getTime() === yesterdayDate.getTime()) {
            // Es ayer: mostrar texto "AYER"
            displayTime = 'AYER';
            displayDate = ''; // No mostrar fecha
        } else {
            // Es anterior: mostrar solo la fecha
            displayTime = ''; // No mostrar hora
            displayDate = messageDate.toLocaleDateString();
        }
    }

    return (
        <Box
            key={contact.id}
            ref={el => setContactRef(contact.id, el)}
            data-contact-id={contact.id}
            sx={{
                padding: 1,
                borderRadius: '3px',
                '&:hover': {
                    backgroundColor: '#2a3942',
                    cursor: 'pointer',
                    padding: 1,
                },
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                // Modificar el fondo cuando está activo o tiene mensajes sin leer
                backgroundColor: isActive
                    ? '#005C4B' // Verde oscuro para el contacto activo
                    : contact.received_messages_count > 0
                        ? 'rgba(42, 57, 66, 0.5)'
                        : 'transparent',
                // Añadir borde izquierdo como indicador adicional
                borderLeft: isActive ? '4px solid #25D366' : 'none',
                // Transición suave
                transition: 'background-color 0.3s, border-left 0.3s',
            }}
            onClick={() => selectUser(contact.id, contact.name, contact.current_mode)}
        >
            {/* Avatar con indicador de estado */}
            <Box sx={{ position: 'relative' }}>
                <Avatar
                    sx={{
                        width: 40,
                        height: 40,
                        marginRight: 1,
                        opacity: notLoadedYet ? 0.6 : 1,
                        // Añadir borde al avatar cuando está activo
                        border: isActive ? '2px solid #25D366' : 'none',
                    }}
                />

                {/* Indicador de activo */}
                {isActive && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -3,
                            right: 3,
                            zIndex: 1,
                        }}
                    >
                        <CheckCircleIcon
                            sx={{
                                fontSize: 16,
                                color: '#25D366',
                                backgroundColor: '#111b21',
                                borderRadius: '50%'
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Box sx={{ width: "100%" }}>
                <Divider sx={{ backgroundColor: '#202c33' }} />
                <ListItemButton
                    sx={{
                        padding: 0,
                        borderRadius: '10px',
                        marginBottom: 0,
                    }}
                >
                    <ListItemText
                        primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography
                                    sx={{
                                        fontSize: isMobile ? "0.775rem" : "0.887rem",
                                        fontWeight: contact.received_messages_count > 0 || isActive ? 'bold' : 'normal',
                                        color: isActive ? '#fff' : undefined,
                                    }}
                                >
                                    {contact.name}
                                </Typography>

                                {contact.received_messages_count > 0 && (
                                    <Box
                                        sx={{
                                            backgroundColor: '#25D366',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {contact.received_messages_count}
                                    </Box>
                                )}
                            </Box>
                        }
                        secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>

                                {/* Fila 1: estado + hora */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <StatusIndicator
                                        isLoading={isLoading}
                                        notLoadedYet={notLoadedYet}
                                        isActive={isActive}
                                    />

                                    {contact.lastMessageTime && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '0.7rem',
                                                color: isActive ? '#C1FFC1' : '#8696a0',
                                                fontWeight: displayTime === 'AYER' ? 'medium' : 'normal',
                                            }}
                                        >
                                            {displayTime || displayDate}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Fila 2: Ejecutivo */}
                                {contact.ejecutivo_cuenta && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.72rem',
                                            color: isActive ? '#E0FFE0' : '#bdbdbd',
                                            marginTop: '2px',
                                        }}
                                    >
                                        Ejecutivo: {contact.ejecutivo_cuenta}
                                    </Typography>
                                )}

                                {/* Fila 3: Número de teléfono */}
                                {contact.phone_number && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.72rem',
                                            color: isActive ? '#E0FFE0' : '#bdbdbd',
                                            marginTop: '2px',
                                        }}
                                    >
                                        {formatPhone(contact.phone_number)}
                                    </Typography>
                                )}

                            </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                        sx={{ color: '#fff', padding: 1 }}
                    />
                </ListItemButton>


                <Divider sx={{ backgroundColor: '#202c33' }} />
            </Box>
        </Box>
    );
};

// Componente para mostrar el estado del chat (Cargando/Chat activo)
const StatusIndicator = ({ isLoading, notLoadedYet, isActive }) => {
    return (
        <Box component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Indicador de carga de mensajes */}
            {isLoading && (
                <Box component="span" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 1,
                    color: '#76c7c0'
                }}>
                    <CircularProgress size={16} thickness={5} sx={{ mr: 0.5, color: '#76c7c0' }} />
                    <Typography variant="caption" sx={{ fontSize: '10px' }}>
                        Cargando mensajes...
                    </Typography>
                </Box>
            )}

            {/* Mostrar "Chat activo" si está activo */}
            {isActive && !isLoading && (
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '10px',
                        fontStyle: 'italic',
                        color: '#25D366'
                    }}
                >
                    Chat activo
                </Typography>
            )}
        </Box>
    );
};

const formatPhone = (phone) => {
    if (!phone) return '';
    // Quitar 521 del inicio si existe
    let cleaned = String(phone).replace(/^521/, '');
    // Dejar solo dígitos
    cleaned = cleaned.replace(/\D/g, '');
    // Formatear en 2-4-4 (ej: 55 1234 5678)
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    }
    return cleaned; // Si no tiene 10 dígitos, mostrar tal cual
};

// Por compatibilidad, mantenemos el componente MessageStatus aunque ya no se use
const MessageStatus = ({ isLoading, notLoadedYet, receivedMessagesCount, isActive }) => {
    return (
        <Box component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Este componente ya no se usa, pero lo mantenemos por compatibilidad */}
            <StatusIndicator isLoading={isLoading} notLoadedYet={notLoadedYet} isActive={isActive} />
        </Box>
    );
};

export default ContactItem;